import type { MouseEvent, ReactNode, Ref } from 'react';
import { motion, type HTMLMotionProps } from 'motion/react';
import { Tooltip } from './Tooltip';
import { cn } from './util';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'success' | 'blue' | 'ink';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-yellow text-ink',
  secondary: 'bg-white text-ink',
  danger: 'bg-team-b text-white [&_.btn-label]:ink-text',
  success: 'bg-jury text-white [&_.btn-label]:ink-text',
  blue: 'bg-team-a text-white [&_.btn-label]:ink-text',
  ink: 'bg-ink text-yellow',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'min-h-8 px-3 py-1 text-[16px] gap-1.5 border-3 shadow-ink-xs',
  md: 'min-h-11 px-4 py-1.5 text-[20px] gap-2 border-3 shadow-ink-sm',
  lg: 'min-h-14 px-6 py-2 text-[26px] gap-2.5 border-4 shadow-ink',
  xl: 'min-h-18 px-8 py-3 text-[34px] gap-3 border-4 shadow-ink-lg',
};

export interface ComicButtonProps extends Omit<HTMLMotionProps<'button'>, 'children' | 'ref'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon (e.g. `<Emoji emoji="▶️" />`). */
  icon?: ReactNode;
  /** Trailing icon. */
  iconRight?: ReactNode;
  /** When disabled, explains why (shown as a tooltip on hover/focus). */
  disabledReason?: ReactNode;
  /** Full width. */
  block?: boolean;
  /** Tilt in degrees for sticker-like buttons. */
  tilt?: number;
  ref?: Ref<HTMLButtonElement>;
  children?: ReactNode;
}

/**
 * Chunky comic button: ink border, hard offset shadow, Bangers label, squash on press.
 * Disabled buttons stay focusable (`aria-disabled`) so their `disabledReason` tooltip is reachable.
 */
export function ComicButton({
  variant = 'primary',
  size = 'md',
  icon,
  iconRight,
  disabled,
  disabledReason,
  block,
  tilt,
  className,
  onClick,
  type = 'button',
  children,
  style,
  ...rest
}: ComicButtonProps) {
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  const button = (
    <motion.button
      type={type}
      aria-disabled={disabled || undefined}
      onClick={handleClick}
      whileTap={disabled ? undefined : { scaleY: 0.86, scaleX: 1.06, y: 2 }}
      transition={{ type: 'spring', stiffness: 700, damping: 18 }}
      className={cn(
        'comic inline-flex items-center justify-center border-ink whitespace-nowrap select-none',
        'transition-[translate,box-shadow,background-color] duration-100',
        SIZE[size],
        disabled
          ? 'bg-[#e7e1d0] text-[#6b6455] shadow-none [&_.btn-label]:[-webkit-text-stroke:0] [&_.btn-label]:[text-shadow:none]'
          : cn(
              VARIANT[variant],
              'hover:-translate-x-px hover:-translate-y-0.5 active:translate-x-0.5 active:translate-y-0.5 active:shadow-none',
            ),
        block && 'w-full',
        className,
      )}
      style={tilt ? { rotate: tilt, ...style } : style}
      {...rest}
    >
      {icon != null && <span className="inline-flex shrink-0 items-center">{icon}</span>}
      {children != null && <span className="btn-label pt-[0.08em]">{children}</span>}
      {iconRight != null && <span className="inline-flex shrink-0 items-center">{iconRight}</span>}
    </motion.button>
  );

  if (disabled && disabledReason) {
    return (
      <Tooltip content={disabledReason} className={block ? 'flex w-full' : undefined}>
        {button}
      </Tooltip>
    );
  }
  return button;
}
